// Copy for the patient consent page.

import { DatabaseBackup, KeyRound, Lock } from "lucide-react";

/**
 * How long the connect steps take. The page shows this text in a pill at the
 * top. It contains a hyphen and not an em dash, because product copy has no
 * em dashes.
 */
export const CONSENT_DURATION = "Takes 3 - 5 mins";

/**
 * Where a patient revokes access later. The path is app-relative. Thus it
 * resolves in each environment. The label names the page and does not show
 * the address.
 */
export const DELETE_PATIENT_DATA_PATH = "/delete-your-data";
export const DELETE_PATIENT_DATA_LABEL = "Delete your data";

/** The inbox for revocation and deletion requests that come by email. */
export const PRIVACY_EMAIL = "privacy@medblocks.com";

/** The text for an acceptance write that fails. No caller supplies it. */
export const ACCEPT_ERROR = "We couldn't save that just now. Please try again.";

/** The text for an exit that fails. No caller supplies it. */
export const LEAVE_ERROR = "We couldn't close this out just now. Please try again.";

/** What happens after the consent page, in one sentence. */
export const WHAT_HAPPENS =
  "Choose the facilities you have received care from, then sign in to each one with the patient portal login you already use.";

/**
 * The consent messages for a patient. The revoke item gives the two ways to
 * revoke. Thus the patient can act on the promise.
 *
 * @param ownRecords true when Medblocks is the only party receiving the records
 * @returns the assurance rows, in reading order
 */
export function assurancesFor(ownRecords: boolean) {
  return [
    {
      Icon: Lock,
      id: "never-sold",
      // Only the own-records reader can be told the records go nowhere else. On
      // the invited path they do reach the organization that asked for them.
      body: ownRecords ? (
        <>
          Your information is never sold. It is shared only with Medblocks Platform, and
          used only for what you approve.
        </>
      ) : (
        <>Your information is never sold, and it&rsquo;s used only for what you approve.</>
      ),
    },
    {
      Icon: KeyRound,
      id: "revoke",
      body: (
        <>
          You can revoke access whenever you want, on the{" "}
          <a
            href={DELETE_PATIENT_DATA_PATH}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2"
          >
            {DELETE_PATIENT_DATA_LABEL}
          </a>{" "}
          page or by emailing{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`} className="underline underline-offset-2">
            {PRIVACY_EMAIL}
          </a>
          .
        </>
      ),
    },
    {
      Icon: DatabaseBackup,
      id: "secure-copy",
      body: <>We keep a secure copy of your records so they stay available to you.</>,
    },
  ] as const;
}
